// @ts-nocheck
import { useState } from 'react';
import axios from 'axios';
import ApiEndpoints from '../const/ApiEndpoints';
/**
 * Custom hook for managing dataset grid operations.
 * @returns {object} Object containing various grid-related functions.
 */
export default function useDatasetGrid() {
  const [tasks, setTasks] = useState([]);
  const [taskCount, setTaskCount] = useState({});
  /**
   * @typedef {object} GridTaskResults
   * @property {object} gridTaskResults - Results of grid task.
   * @property {object} highlightCells - Highlighted cells information.
   * @property {string} currentTaskId - Current task's ID.
   * @property {string} taskOperationName - Name of the task operation.
   * @property {string} isPaginated - Pagination status.
   * @property {string} allData - All data associated with the task.
   */

  /**
   * @typedef {object} ApiResponse
   * @property {object} response - Response object from API call.
   */
  const [gridTaskResultsData, setGridTaskResultsData] = useState({
    gridTaskResults: {},
    highlightCells: {},
    currentTaskId: '',
    taskOperationName: '',
    isPaginated: '',
    allData: '',
  });
  /**
   * Resets the grid task results data to initial state.
   * @function resetGridTaskResultsData
   */
  const resetGridTaskResultsData = () => {
    setGridTaskResultsData({
      gridTaskResults: {},
      highlightCells: {},
      currentTaskId: '',
      taskOperationName: '',
      isPaginated: '',
      allData: '',
    });
  };

  /**
   * @function renameColumn
   * @description -  renaming column name in grid
   * @param {string} oldcolumnName - old name of column before rename
   * @param {string} newcolumnName - new name of column after rename
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @param {number} projectId - ID of the project. 
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully by api call
   * @example renameColumn(
    oldColumnName,
    newColumnName,
    datasetId,
    successCallback,
    errorCallback
  );
   */
  const renameColumn = (
    oldColumnName,
    newColumnName,
    datasetId,
    projectId,
    workerName
  ) =>
    axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.RENAME_COLUMN}`,
        {
          column_name: oldColumnName,
          new_column_name: newColumnName,
          dataset_id: datasetId,
          project_id: projectId,
          ...(workerName && { worker_name: workerName }),
        }
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function deleteColumn
   * @description -  deleting column in grid of a specific dataset
   * @param {string} columnName - name of column
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @param {number} projectId - The identifier of the project associated with the dataset. 
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @throws Will throw an error if the API call is unsuccessful.
   * @example deleteColumn (
    columnName,
    datasetId,
    successCallback,
    errorCallback
  );
   */

  const deleteColumn = (columnName, datasetId, projectId, workerName) =>
    axios
      .delete(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.DELETE_COLUMN}`,
        {
          data: {
            column_name: columnName,
            dataset_id: datasetId,
            project_id: projectId,
            ...(workerName && { worker_name: workerName }),
          },
        }
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function fetchGridTaskData
   * @description Fetches task-related data when the "View Result" button is clicked in the job queue.
   * @param {object} data - Task details object.
   * @param {Event} e - Event object.
   * @param {number} pageIndex - Page index for paginated results.
   * @param {number} pageSize - Page size for paginated results.
   * @returns {Promise<object>} A promise that resolves with the fetched task data.
   * @example const taskData = await fetchGridTaskData(data, e, pageIndex, pageSize);
   */
  const fetchGridTaskData = (data, e, pageIndex, pageSize) => {
    if (!gridTaskResultsData?.isPaginated) {
      resetGridTaskResultsData();
    }
    return axios
      .get(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.FETCH_TASKS}/${data?.task_id}`,
        {
          params: {
            ...(pageSize ? { page_size: pageSize } : {}),
            ...(pageIndex?.toString() ? { page: pageIndex + 1 } : {}),
          },
        }
      )
      .then(
        (res) => {
          if (res.data.task_status === 'SUCCESS') {
            setGridTaskResultsData({
              taskOperationName: data?.operation,
              currentTaskId: data?.task_id,
              gridTaskResults: res?.data,
              highlightCells: {
                pks: res?.data?.results?.response?.pks,
                columns: res?.data?.parameters?.column_name
                  ? [res?.data?.parameters?.column_name?.toLowerCase()]
                  : null,
                duplicateColumns: res?.data?.results?.response?.columns,
                scoresData: res?.data?.results?.pds
                  ? res?.data?.results?.tables[0]?.data
                  : [],
              },
              isPaginated:
                res?.data?.results?.is_paginated || res?.data?.results?.rss_pds,
              allData: data,
            });
          }
          return res.data;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function duplicateColumn
   * @description -  duplicate column in grid
   * @param {object} dataType - type of data
   *+ @param {string} duplicateColumnName - name of duplicate column in grid
   * @param {string} columnName - name of column
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @param {number} projectId - ID of the project containing the dataset.
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example duplicateColumn (
    columnName,
    duplicateColumnName,
    datasetId,
    successCallback,
    errorCallback
  );
   */
  const duplicateColumn = (
    columnName,
    duplicateColumnName,
    datasetId,
    projectId,
    workerName
  ) =>
    axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.DUPLICATE_COLUMN}`,
        {
          column_name: columnName,
          new_column_name: duplicateColumnName,
          dataset_id: datasetId,
          project_id: projectId,
          ...(workerName && { worker_name: workerName }),
        }
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function addColumn
   * @description -  for adding column in grid
   * @param {string} columnName - Name of column
   * @param {object} dataType - Type of data
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @param {number} projectId - ID of the project containing the dataset.
   * @callback successCallback Handles response on successful api call
   * @callback errorCallback Handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @throws Throws an error if the API call fails. 
   * @example addColumn(
    columnName,
    dataType,
    datasetId,
    successCallback,
    errorCallback
  );
   */
  const addColumn = (columnName, dataType, datasetId, projectId, workerName) =>
    axios
      .put(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.ADD_COLUMN}`,
        {
          column_name: columnName,
          data_type: dataType,
          dataset_id: datasetId,
          project_id: projectId,
          ...(workerName && { worker_name: workerName }),
        }
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function deleteRow
   * @description -  For deleting a row in grid
   * @param {number} rowId - id of row
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @param {number} projectId - The ID of the project to which the dataset belongs.
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @throws {Error} Throws an error if the API call is unsuccessful.
   * @example deleteRow(rowId, datasetId, successCallback, errorCallback);
   */

  const deleteRow = (rowId, datasetId, projectId, values) =>
    axios
      .delete(ApiEndpoints.MODULE_BASE_URL.DM + ApiEndpoints.GRID.DELETE_ROW, {
        data: {
          row_id: rowId,
          dataset_id: datasetId,
          project_id: projectId,
          ...(values.workerName?.worker_name && {
            worker_name: values.workerName?.worker_name,
          }),
        },
      })
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function duplicateRow
   * @description -  Duplicate row
   * @param {number} rowId - id of row
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @param {number} projectId - The ID of the project to which the dataset belongs.
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example duplicateRow(rowId, datasetId, successCallback, errorCallback);
   */
  const duplicateRow = (rowId, datasetId, projectId, workerName) =>
    axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.DUPLICATE_ROW}`,
        {
          row_id: rowId,
          dataset_id: datasetId,
          project_id: projectId,
          ...(workerName && { worker_name: workerName }),
        }
      )
      .then(
        (resp) => {
          return resp;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function addRow
   * @description -  For adding row in grid
   * @param {string} newRowData - The data of the columns in the new row.
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @param {number} projectId - The ID of the project to which the dataset belongs.
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example const addRow(newRowData, datasetId, successCallback, errorCallback);
   */
  const addRow = (newRowData, datasetId, projectId, workerName) =>
    axios
      .put(`${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.ADD_ROW}`, {
        values: newRowData,
        dataset_id: datasetId,
        project_id: projectId,
        ...(workerName && { worker_name: workerName }),
      })
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function fetchTasks
   * @description -  For Fetching tasks in Task Manager
   * @param {number} projectId - project id
   * @param {number} datasetId - dataset id
   * @returns {object} response that contains the results generated on successfully api call
   * @example fetchTasks(datasetId, projectId);
   */
  const fetchTasks = (
    datasetId,
    projectId,
    pageIndex,
    pageSize,
    taskStatus,
    count = false,
    hideLoaderOnRequest
  ) => {
    return axios
      .get(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.FETCH_TASKS}`,
        {
          params: {
            dataset_id: datasetId,
            project_id: projectId,
            ...(pageIndex ? { page_index: pageIndex } : null),
            ...(pageSize ? { page_size: pageSize } : {}),
            ...(taskStatus ? { task_status: taskStatus } : {}),
            ...(count ? { count } : {}),
          },
          loader: !hideLoaderOnRequest,
        }
      )
      .then((res) => {
        if (count) {
          setTaskCount(res.data?.count);
        } else if (!count && pageIndex > 1) {
          setTasks((prevTasks) => [...prevTasks, ...(res?.data?.tasks || [])]);
        } else {
          setTasks(res.data?.tasks || []);
        }
        return res;
      });
  };

  /**
   * @function terminateTask
   * @description -  For Terminating Task in Task Manager
   * @param {string} taskID - task id
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example terminateTask(taskID, successCallback, errorCallback);
   */

  const terminateTask = (taskID) =>
    axios
      .delete(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.FETCH_TASKS}/${taskID}`
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function manageSpaces
   * @description -  ManageSpaces column level operation of grid
   * @param {object} manageSpacesPayload - manages spaces of the payload of grid
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example manageSpaces(
    manageSpacesPayload,
    datasetId,
    successCallback,
    errorCallback
  );
   */
  const manageSpaces = (manageSpacesPayload, datasetId) =>
    axios
      .put(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.MANAGE_SPACES}`,
        { ...manageSpacesPayload, dataset_id: datasetId }
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function manageCases
   * @description - ManageCases column level operation of grid
   * @param {object} manageCasesPayload - manages cases of the payload
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example manageCases(
    manageCasesPayload,
    datasetId,
    successCallback,
    errorCallback
  );
   */
  const manageCases = (manageCasesPayload, datasetId) =>
    axios
      .patch(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.MANAGE_CASES}`,
        { ...manageCasesPayload, dataset_id: datasetId }
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function splitColumn
   * @description - SplitColumn column level operation
   * @param {object} splitColumnPayload - splitting the column of payload
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example splitColumn(
    splitColumnPayload,
    datasetId,
    successCallback,
    errorCallback
  );
   */
  const splitColumn = (splitColumnPayload, datasetId) =>
    axios
      .patch(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.SPLIT_COLUMN}`,
        { ...splitColumnPayload, dataset_id: datasetId }
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function findAndReplace
   * @description Find And Replace column level operation of grid
   * @param {object} findAndReplacePayload  - Finding and then Replacing payload
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example const findAndReplace(
    findAndReplacePayload,
    datasetId,
    successCallback,
    errorCallback
  );
   */

  const findAndReplace = (findAndReplacePayload, datasetId) =>
    axios
      .patch(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.FIND_AND_REPLACE}`,
        { ...findAndReplacePayload, dataset_id: datasetId }
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function sortColumn
   * @description - sortcolumn column level operation of grid
   * @param {object} sortPayload it sorts payload of payload level operation of grid
   * @param {number} datasetId - fetchingdataset rows with pagination of particular dataset id
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example sortColumn(
    sortPayload,
    datasetId,
    successCallback,
    errorCallback
  );
   */
  const sortColumn = (sortPayload, datasetId) =>
    axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.SORT_COLUMN}`,
        { ...sortPayload, dataset_id: datasetId }
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function analyse
   * @description - Performs data-management >> data-quality >>  analyse on grid
   * @param  {object} analysePayload data we provide to get analyse result for grid
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example analyse(analysePayload, successCallback, errorCallback);
   */
  const analyse = (analysePayload) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DM + ApiEndpoints.GRID.ANALYSE,
        analysePayload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function embeddedValues
   * @description - Performs data-management >> data-quality >> Embedded Values on grid
   * @param  {object} embeddedValuesPayload data we provide to get Embedded values result for grid
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example response = {
   * "charts": graphs and charts related data,
   * "tables": tables data,
   * "is_chart": {true/false} flag used to determine if the response contains any charts or not,
   * "is_table": {true/false} flag used to determine if the response contains any tables or not,
   * "response": contains pks of all the rows that needs to be highlighted
   * }
   */
  const embeddedValues = (embeddedValuesPayload) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DM + ApiEndpoints.GRID.EMBEDDED_VALUES,
        embeddedValuesPayload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function standardisationError
   * @description - Performs data-management >> data-quality >> Standardisation Error on grid
   * @param  {string} standardisationErrorEndpoint to set API endpoint based on the operation selected
   * @param  {object} standardisationErrorPayload data we provide to get Standardisation Error result for grid
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example response = {
   * "is_chart": {true/false} flag used to determine if the response contains any charts or not,
   * "is_table": {true/false} flag used to determine if the response contains any tables or not,
   * "response": contains pks of all the rows that needs to be highlighted
   * }
   */
  const standardisationError = (
    standardisationErrorEndpoint,
    standardisationErrorPayload
  ) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DM +
          ApiEndpoints.GRID[standardisationErrorEndpoint],
        standardisationErrorPayload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function integrityRules
   * @description - Performs data-management >> data-quality >> Integrity Rules on grid
   * @param  {string} integrityRulesEndpoint to set API endpoint based on the operation selected
   * @param  {object} integrityRulesPayload data we provide to get Integrity Rules result for grid
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   *@example response = {
   * "Type": "int",
   * "charts": graphs and charts related data,
   * "tables": tables data,
   * "is_chart": {true/false} flag used to determine if the response contains any charts or not,
   * "is_table": {true/false} flag used to determine if the response contains any tables or not,
   * "response": contains pks of all the rows that needs to be highlighted,
   * "table_id":
   * }
   *
   */
  const integrityRules = (integrityRulesEndpoint, integrityRulesPayload) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DM +
          ApiEndpoints.GRID[integrityRulesEndpoint],
        integrityRulesPayload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function uniqueness
   * @description - Performs data-management >> data-validation >> Embedded Values on grid
   * @param  {object} uniquenessPayload data we provide to get uniqueness result for grid
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} response that contains the results generated on successfully api call
   * @example response = {
   * "is_chart": {true/false} flag used to determine if the response contains any charts or not,
   * "is_table": {true/false} flag used to determine if the response contains any tables or not,
   * "response": contains pks of all the rows that needs to be highlighted,
   * }
   */
  const uniqueness = (uniquenessPayload) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DM + ApiEndpoints.GRID.UNIQUENESS,
        uniquenessPayload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function scores
   * @description - Performs data-management >> data-validation >> scores on grid
   * @param  {string} scoresEndpoint - to set API endpoint based on the operation selected
   * @param  {object} scoresPayload - data we provide to get uniqueness result for grid
   * @callback successCallback handles response on successful api call
   * @callback errorCallback handles errors on unsuccessful api call
   * @returns {object} - response that contains the results generated on successfully api call
   * @example response = {
   * "charts": {array} graphs and charts related data,
   * "tables":  {array} tables data,
   * "is_chart": {boolean} flag used to determine if the response contains any charts or not,
   * "is_table": {boolean} flag used to determine if the response contains any tables or not,
   * "table_id": {array} array containing id's of tables that can be used for comments.
   * "response" : contains score for dfcs and pds
   * }
   */
  const scores = (scoresEndpoint, scoresPayload) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DM + ApiEndpoints.GRID[scoresEndpoint],
        scoresPayload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function findDuplicateColumns
   * @description - Performs findDuplicateColumns on grid
   * @param  {object} findDuplicateColumnsPayload - payload object for findDuplicateColumns operation
   * @returns {object} - response that contains the results generated on successfully api call
   * @example findDuplicateColumns(findDuplicateColumnsPayload)
   */
  const findDuplicateColumns = (findDuplicateColumnsPayload) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DM +
          ApiEndpoints.GRID.FIND_DUPLICATE_COLUMN,
        findDuplicateColumnsPayload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function findDuplicateRows
   * @description - Performs findDuplicateRows on grid
   * @param  {object} findDuplicateRowsPayload - payload object for findDuplicateRows operation
   * @returns {object} - response that contains the results generated on successfully api call
   * @example findDuplicateRows(findDuplicateRowsPayload)
   */
  const findDuplicateRows = (findDuplicateRowsPayload) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DM + ApiEndpoints.GRID.FIND_DUPLICATE_ROW,
        findDuplicateRowsPayload
      )
      .then(
        (resp) => resp?.data?.rows,
        (error) => {
          throw error;
        }
      );

  /**
   * @function findAndDeleteDuplicateRows
   * @description - Performs findAndDeleteDuplicateRows on grid
   * @param  {object} findAndDeleteDuplicateRowsPayload - payload object for findAndDeleteDuplicateRows operation
   * @returns {object} - response that contains the results generated on successfully api call
   * @example findAndDeleteDuplicateRows(findAndDeleteDuplicateRowsPayload)
   */
  const findAndDeleteDuplicateRows = (
    findAndDeleteDuplicateRowsPayload,
    datasetId
  ) =>
    axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.FIND_AND_DELETE_DUPLICATE_ROWS}`,
        { ...findAndDeleteDuplicateRowsPayload, dataset_id: datasetId }
      )
      .then(
        (resp) => resp?.data,
        (error) => {
          throw error;
        }
      );

  /**
   * Deletes rows with duplicate values in specified columns from the grid dataset.
   *
   * @function findAndDeleteDuplicateColumns
   * @description - Performs findAndDeleteDuplicateColumns on grid
   * @param  {object} findAndDeleteDuplicateColumnsPayload - payload object for findAndDeleteDuplicateColumns operation
   * @param {number} datasetId - The ID of the dataset on which the operation will be performed.
   * @param {string[]} columnNames - An array of column names to check for duplicates.
   * @param {function} successCallback - A callback function to handle successful API response.
   * @param {function} errorCallback - A callback function to handle errors during API call.
   * @returns {object} - response that contains the results generated on successfully api call
   * @example findAndDeleteDuplicateColumns(findAndDeleteDuplicateColumnsPayload)
   */
  const findAndDeleteDuplicateColumns = (
    findAndDeleteDuplicateColumnsPayload,
    datasetId
  ) =>
    axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.DM}${ApiEndpoints.GRID.FIND_AND_DELETE_DUPLICATE_COLUMNS}`,
        { ...findAndDeleteDuplicateColumnsPayload, dataset_id: datasetId }
      )
      .then(
        (resp) => resp?.data?.rows,
        (error) => {
          throw error;
        }
      );

  /**
   * @function markTaskReadUnreadGrid
   * @description - for changing read status of a task in the job queue grid section
   * @param  {object} taskParams - task details required to change read status
   * @returns {object} - response of markTaskReadUnreadGrid api
   * @example markTaskReadUnreadGrid(taskParams)
   */
  const markTaskReadUnreadGrid = (taskParams) =>
    axios
      .get(
        `${
          ApiEndpoints.MODULE_BASE_URL.DM +
          ApiEndpoints.GRID.JOB_QUEUE_MARK_READ_UNREAD
        }`,
        {
          params: taskParams,
        }
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  /**
   * @function valueError
   * @description - Performs valueErrorPayload on grid
   * @param  {object} valueErrorPayload - payload object for valueError operation
   * @returns {object} - response that contains the results generated on successfully api call
   * @example valueError(valueErrorPayload)
   */
  const valueError = (valueErrorPayload) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DM + ApiEndpoints.GRID.VALUE_ERROR,
        valueErrorPayload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function findBlankValuePercentage
   * @description - Performs findBlankValuePercentage on grid
   * @param  {object} findDuplicateColumnsPayload - payload object for findBlankValuePercentage operation
   * @returns {object} - response that contains the results generated on successfully api call
   * @example findBlankValuePercentage(findBlankValuePercentagePayload)
   */
  const findBlankValuePercentage = (findBlankValuePercentagePayload) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DM +
          ApiEndpoints.GRID.FIND_BLANK_VALUE_PERCENTAGE,
        findBlankValuePercentagePayload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function replaceBlankValues
   * @description - Performs replace blank values on grid
   * @param  {object} replaceBlankValuesPayload - payload object for replaceBlankValues operation
   * @returns {object} - response that contains the results generated on successfully api call
   * @example replaceBlankValues(replaceBlankValuesPayload)
   */
  const replaceBlankValues = (replaceBlankValuesPayload) =>
    axios
      .patch(
        ApiEndpoints.MODULE_BASE_URL.DM +
          ApiEndpoints.GRID.REPLACE_BLANK_VALUES,
        replaceBlankValuesPayload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function findRegexPercentage
   * @description - Performs Regex Percentage on grid
   * @param  {object} findDuplicateColumnsPayload - payload object for findRegexPercentage operation
   * @returns {object} - response that contains the results generated on successfully api call
   * @example findRegexPercentage(findRegexPercentagePayload)
   */
  const findRegexPercentage = (findRegexPercentagePayload) =>
    axios
      .post(
        ApiEndpoints.MODULE_BASE_URL.DM +
          ApiEndpoints.GRID.FIND_REGEX_PERCENTAGE,
        findRegexPercentagePayload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   *
   * Fetches paginated data for a specific table in the grid.
   * @function fetchTableDataResult
   * @param {string} tableId - The unique identifier of the table for which data needs to be fetched.
   * @param {number} pageNumber - The page number of the data to fetch (starting from 0).
   * @param {number} pageSize - The number of items per page to fetch.
   * @returns {Promise} A Promise that resolves to the response containing paginated data or rejects with an error.
   * @throws {Error} Throws an error if there's an issue with the API request or response.
   */
  // eslint-disable-next-line camelcase
  const fetchTableDataResult = (project_id, tableId, pageNumber, pageSize) =>
    axios
      .get(
        `${ApiEndpoints.MODULE_BASE_URL.DM + ApiEndpoints.GRID.GRID_TABLE}`,
        {
          params: {
            // eslint-disable-next-line camelcase
            project_id,
            table_id: tableId,
            page: pageNumber + 1,
            pagesize: pageSize,
          },
        }
      )
      .then(
        (res) => res,
        (err) => {
          throw err;
        }
      );

  /**
   * Rerun a Grid Operation
   * @function gridOperationRerun
   * @description Initiates the rerun of a previously executed grid operation.
   * @param {string} operationId - The ID of the operation to be rerun.
   * @returns {Promise<Object>} A promise that resolves with the response data upon successful rerun,
   * and rejects with an error in case of an unsuccessful rerun.
   * @throws {Error} If there is an issue with the API request or execution.
   */
  const gridOperationRerun = (id) => {
    return axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.DM + ApiEndpoints.GRID.RERUN_OPERATION
        }`,
        id
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  };
  const fetchAsyncResultSplit = (taskID, type) =>
    axios
      .get(
        `${ApiEndpoints.MODULE_BASE_URL.DM + ApiEndpoints.GRID.FETCH_TASKS}/${taskID}/${type}`
      )
      .then((res) => {
        return res;
      });

  return {
    renameColumn,
    deleteColumn,
    duplicateColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    tasks,
    taskCount,
    fetchTasks,
    terminateTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    gridOperationRerun,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    fetchTableDataResult,
    fetchAsyncResultSplit,
  };
}
